"use client";
import Image from "next/image";
import { useState } from 'react';
import { avenirNextRegular, avenirNextMedium } from '@/styles/fonts';
import clsx from 'clsx';
import AccordionArrow from "./accordionArrow";
import browserTour2 from '@/app/images/tour/browsertour2.png';
import adCreative from '@/app/images/tour/adcreative.png';
import campaign2 from '@/app/images/tour/campagin2.png';
import publisher2 from '@/app/images/tour/publisher2.png';

const menuItems = [
  {
    title: "Learn Media Buying Strategy",
    content: `The Advertiser Dashboard gives you a bird’s eye view of any advertiser’s media buying strategy. See their most successful
      publishers, networks, ad types and more. Uncover new traffic sources and placements
      that are already working for advertisers in your industry, plus estimated ad spend
      down to the dollar amount.`,
    image: browserTour2,
    alt: "Browser Tour",
    className: "lg:relative lg:bottom-[200px]",
  },
  {
    title: "Write Higher Converting Ad Creative",
    content: `Develop your creative strategy with insights from the Top Ads tab. Browse all creatives and formats -- image, text, flash,
      and text-image creatives -- for any advertiser. You’ll also get the flight dates,
      trends, landing pages, and specific publishers for each creative.
      <br /><br /> Hover over and download any creative in our database. Use these ads to help
      inspire your internal team or impress clients by sending them the exact creatives
      that their biggest competitors are successfully using.`,
    image: adCreative,
    alt: "Ad Creative",
  },
  {
    title: "Campaigns",
    content: `Big brand advertisers often have multiple products and ad campaigns. See ad campaigns by product and modify your strategy
      accordingly.`,
    image: campaign2,
    alt: "Campaign",
  },
  {
    title: "Publisher Data",
    content: `Adbeat's Top Publisher tab shows you which publishers your competitors buy the most traffic from. Leverage this information
      to spend smarter by testing out publishers that have already been proven to work
      for the competition.`,
    image: publisher2,
    alt: "Publisher",
  },
];

export default function TourBodyAccordeon() {
  const [tab, setTab] = useState(0);
  const toggleTab = (index: number) => {
    setTab(tab === index ? -1 : index);
  };

  const liClassName = clsx(
    avenirNextRegular.className,
    "text-[16px]",
    "text-[#000000]",
    "mb-[20px]",
    "w-full",
    "border-t",
    "border-[#d8d8d8]"
  );
  const menuItemClassName = clsx(
    avenirNextMedium.className,
    "text-[22px]",
    "text-[#343f50]",
    "cursor-pointer",
    "pt-[36px]",
    "pb-[8px]",
    "sm:pt-[37px]",
    "sm:pb-[16px]",
    "pl-[15px]",
    "flex justify-between",
  );
  const itemClassName = clsx(
    "text-[#5e6977]",
    "text-[18px]",
    "pl-[15px]",
    "leading-[1.8]",
    "transition-all",
    "duration-500"
  );

  return (
    <section className={clsx(
      "w-full",
      "text-[18px]",
      avenirNextRegular.className,
      "grid",
      "text-[#52575b]",
      "pt-[15px] sm:pt-[35px]",
      "pb-[80px]",
      "text-left",
      "xs:grid-cols-1",
      "lg:grid-cols-[minmax(0,1fr)_minmax(0,400px)_minmax(0,400px)_minmax(0,1fr)]",
      "lg:gap-x-[40px]",
      "lg:items-start",
      "gap-[20px]",
      "overflow-visible",
      )}>
      <div className="lg:col-start-2 lg:col-end-3">
        <h2 className={clsx(
          avenirNextMedium.className,
          "text-[20px] sm:text-[30px]",
          "px-[15px]",
          "text-[#444847]",
          "pb-[17px] sm:pb-[34px]",
        )}>Competitive Monitoring</h2>
        <ul className="sm:px-[15px]">
          {menuItems.map((item, index) => (
            <li key={index} className={liClassName}>
              <div className={menuItemClassName} onClick={() => toggleTab(index)}>
                <div>{item.title}</div>
                <AccordionArrow direction={tab === index ? "down" : "right"} />
              </div>
              <div className={clsx(itemClassName)} style={{
                maxHeight: tab === index ? "1000px" : "0",
                opacity: tab === index ? 1 : 0,
                paddingBottom: tab === index ? "15px" : "0",
                pointerEvents: tab === index ? "auto" : "none",
              }}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-[20px] items-start w-full">
                  <div dangerouslySetInnerHTML={{ __html: item.content }}
                   className="pr-[15px] leading-[1.1] sm:text-[18px] sm:leading-[1.6]" />
                  <div className="w-full flex justify-end mr-[-50px] md:mr-0 lg:hidden">
                    <Image
                      src={item.image}
                      alt={item.alt}
                      className="img-responsive accordian-img w-1/2 sm:w-full h-auto"
                    />
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
      {/* Right column: active item image, lg+ only */}
      <div className="hidden 
        lg:flex 
        lg:col-start-4
        lg:col-end-5
        lg:items-start 
        lg:justify-end 
        lg:overflow-visible
        ">
        {tab >= 0 && (
          <Image
            src={menuItems[tab].image}
            alt={menuItems[tab].alt}
            className={`"w-full h-auto lg:max-w-[560px] 
              lg:z-10
              lg:min-w-[500px] ${menuItems[tab].className || ""}`}
          />
        )}
      </div>
    </section>
  );
}
